import React from 'react';
import PropTypes from 'prop-types';
import { Box, Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton } from '@mui/material';
import { CinchActionButton } from '../buttons/cinch-action-button.component';
import { CloseIcon } from '../../../components/mui/icon';
import { CinchActionButtonText } from '../buttons/cinch-action-button-text.component';

/**
 * A custom MUI Warning Dialog Component that is used to alert the user.
 *
 * @param {string} title - The title of the warning.
 *
 * @param {string} bodyText - The prompt of the warning.
 *
 * @param {string} size - The size of the dialog. Options are "sm", "md", "lg", default is H:400px, W:500px.
 *
 * @param {function} onCancel - The action that the cancel button will perform on click.
 *
 * @param {function} onConfirm - The action that the confirm button will perform on click.
 *
 * @param {boolean} open - The open condition state of the component.
 *
 * @param {string} type - The type of button. Options are "cancel" or "confirm".
 *
 * @param {function} onClick - The action that the button will perform on click.
 *
 */

export const CinchWarningDialog = props => {
  return <>
      <Dialog slotProps={{
      paper: {
        sx: {
          borderRadius: '28px',
          height: props.customHeight || (props.size === 'sm' ? '250px' : props.size === 'md' ? '300px' : props.size === 'lg' ? '500px' : '400px'),
          width: props.customWidth || (props.size === 'sm' ? '320px' : props.size === 'md' ? '400px' : props.size === 'lg' ? '600px' : '500px'),
          pb: '10px',
          overflow: 'hidden'
        }
      }
    }} maxWidth={props.customWidth ? false : undefined} open={props.open} onClose={() => props.onCancel}>
        <DialogTitle>
          {props.title || 'Warning'}
          <IconButton aria-label="close" onClick={props.onCancel} sx={{
          position: 'absolute',
          right: '10px',
          top: '10px'
        }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Box sx={{
        pb: '8px'
      }}>
          <Divider />
        </Box>
        <DialogContent sx={{
        p: '8px 24px'
      }}>{props.bodyText}</DialogContent>
        <Box sx={{
        pt: '20px',
        pb: '20px'
      }}>
          <Divider />
        </Box>
        <DialogActions sx={{
        pb: '10px'
      }}>
          <CinchActionButton isCancel={true} text="Cancel" onClick={props.onCancel} />
          <CinchActionButton onClick={props.onConfirm} />
        </DialogActions>
      </Dialog>
    </>;
};
CinchWarningDialog.propTypes = {
  /** The title of the warning. */
  title: PropTypes.string,
  /** The prompt of the warning. */
  bodyText: PropTypes.node,
  /**  The size of the dialog. Options are "sm", "md", "lg", default is H:400px, W:500px.  */
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  /** The action that the button will perform on click. */
  onCancel: PropTypes.func.isRequired,
  /** The action that the button will perform on click. */
  onConfirm: PropTypes.func.isRequired,
  /**  The open condition state of the component. */
  open: PropTypes.bool.isRequired,
  /** Custom height override for the dialog (e.g. "350px"). */
  customHeight: PropTypes.string,
  /** Custom width override for the dialog (e.g. "700px"). */
  customWidth: PropTypes.string
};
CinchWarningDialog.defaultProps = {
  size: 'null'
};